/**
 * B-44.1 "Find a tour" — Marakin-curated rugby union festivals, tournaments
 * and tour packages. Read-only, bundled with the app (no table, no API).
 *
 * Source: C:\dev\marakin-marketing\planning\2026-10-14_marakin_tour-listing-research.md
 * (each row checked on the organiser's own page on `lastChecked`). The
 * researched fields are copied as they are; four are added for the page:
 *
 *   startDate / endDate  ISO dates transcribed from `dates` (null when the
 *                        organiser hasn't published them)
 *   usualMonths          for "dates TBC" rows, the months the organiser
 *                        usually runs it ("usually Apr–May") — drives the
 *                        month filter only; the card still says "dates TBC"
 *   status               'open' — listed
 *                        'closed' — applications shut; kept here so it's easy
 *                                   to re-open next season, never listed
 *                        'recheck' — not listed until someone checks the
 *                                   organiser's page by hand (research caveat)
 *
 * Rows also drop off the page by themselves once `endDate` has passed.
 * When re-checking a row, update `lastChecked` (YYYY-MM-DD).
 * Re-check 14 Oct 2026 (QA): every `datesConfirmed` row fetched again from
 * its organiser page. Hilversum corrected (organiser says "2027 dates TBC",
 * U13–U18, no package dates); Bournemouth 7s downgraded (site still 403).
 * WT-26 (16 Oct walk-through): `format` (plus Hilversum's dates and
 * organiser, and Rayburn's dates) reworded in plain words for a club
 * volunteer: same facts, no research shorthand ("format not stated",
 * "Tournify", "marked TBC"). A detail the organiser hasn't given says
 * "check with the organiser". Unconfirmed dates get the card's own
 * "Dates TBC" badge (lib/tourListing needsTbcLabel), so `dates` needn't say so.
 * Pure data: no imports, so backend `node --test` can load it.
 */

// The RFU's own "Trips, Tours and Overnight Stays" safeguarding document on
// englandrugby.com (linked from the RFU helpdesk article cited in the
// research; resolved 14 Oct 2026). help.rfu.com has no public copy.
export const RFU_TOURING_GUIDANCE_URL =
  'https://www.englandrugby.com/dxdam/c1/c19cb3d3-55e8-4e33-a852-444841098db6/safeguardingtour.pdf';

export const TOURS_SUGGEST_EMAIL = 'support@marakin.co.uk';

export const TOURS = [
  {
    id: "old-redcliffians-mini-junior-2027",
    name: "Old Redcliffians Mini & Junior Rugby Festival 2027",
    organiser: "Old Redcliffians RFC",
    type: "festival",
    dates: "10–11 Apr 2027",
    datesConfirmed: true,
    place: "Bristol",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U14","U15"],
    format: "Minis play a round robin under RFU age-grade rules. U13–U15 boys play a 10-a-side tournament.",
    url: "https://www.tickettailor.com/events/oldredcliffiansrfc/2281804",
    sourceUrl: "https://www.tickettailor.com/events/oldredcliffiansrfc/2281804",
    lastChecked: "2026-10-14",
    startDate: "2027-04-10",
    endDate: "2027-04-11",
    status: "open",
  },
  {
    id: "bournemouth-rugby-youth-minis-2027",
    name: "Bournemouth Rugby Youth & Minis Festival 2027",
    organiser: "Bournemouth Rugby",
    type: "festival",
    dates: "3–4 Apr 2027",
    datesConfirmed: true,
    place: "Christchurch, Dorset",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16"],
    format: "U7–U11 play a round robin under the RFU’s Kids First rules. U12–U16 play group games, then play-offs.",
    url: "https://bournemouth-rugby.co.uk/2027-youth-minis-festival-information/",
    sourceUrl: "https://bournemouth-rugby.co.uk/2027-youth-minis-festival-information/",
    lastChecked: "2026-10-14",
    startDate: "2027-04-03",
    endDate: "2027-04-04",
    status: "open",
  },
  {
    id: "looseheadz-future-stars-2027",
    name: "Future Stars Festival 2027",
    organiser: "LooseHeadz (hosted at Camelot RUFC)",
    type: "tournament",
    dates: "24–25 Apr 2027",
    datesConfirmed: true,
    place: "Hemel Hempstead, Hertfordshire",
    country: "England",
    ageGroups: ["U11 mixed","U12 boys","U12 girls","U14 girls"],
    format: "Trophy, Cup and Plate competitions. No entry fee: each team raises money for LooseHeadz instead.",
    url: "https://looseheadz.co.uk/?p=8354",
    sourceUrl: "https://looseheadz.co.uk/?p=8354",
    lastChecked: "2026-10-14",
    startDate: "2027-04-24",
    endDate: "2027-04-25",
    status: "open",
  },
  {
    id: "london-irish-amateur-mini-2027",
    name: "London Irish Amateur International Mini Rugby Festival 2027",
    organiser: "London Irish Amateur RFC",
    type: "festival",
    dates: "24 Apr 2027 (U12); 25 Apr 2027 (minis)",
    datesConfirmed: true,
    place: "Sunbury-on-Thames, Surrey",
    country: "England",
    ageGroups: ["Minis","U12"],
    format: "Minis festival with teams from several countries. Register your interest on the organiser’s page.",
    url: "https://www.liarfc.co.uk/a/london-irish-amateur-international-mini--under-12-rugby-festival-weekend-55897.html?page=1",
    sourceUrl: "https://www.liarfc.co.uk/a/london-irish-amateur-international-mini--under-12-rugby-festival-weekend-55897.html?page=1",
    lastChecked: "2026-10-14",
    startDate: "2027-04-24",
    endDate: "2027-04-25",
    status: "open",
  },
  {
    id: "wimbledon-spring-minis-2027",
    name: "Wimbledon RFC Spring Minis Festival 2027",
    organiser: "Wimbledon RFC",
    type: "festival",
    dates: "7 Mar 2027",
    datesConfirmed: true,
    place: "Wimbledon, London",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11"],
    format: "Played on full-size pitches. Medals for the younger age groups, trophies for the older ones. The organiser shares the match schedule online.",
    url: "https://wmrfc.co.uk/wimbledon-rfc-minis-rugby-festivals",
    sourceUrl: "https://wmrfc.co.uk/wimbledon-rfc-minis-rugby-festivals",
    lastChecked: "2026-10-14",
    startDate: "2027-03-07",
    endDate: "2027-03-07",
    status: "open",
  },
  {
    id: "international-mini-youth-series-chichester-2027",
    name: "International Mini & Youth Rugby Series 2027",
    organiser: "International Mini Rugby Series (Chichester College)",
    type: "festival",
    dates: "1–2 May 2027",
    datesConfirmed: true,
    place: "Chichester, West Sussex",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13"],
    format: "League format, with 50–80 minutes of rugby a day. You need a pre-paid ticket and wristband.",
    url: "https://www.internationalminirugby.com/",
    sourceUrl: "https://www.internationalminirugby.com/",
    lastChecked: "2026-10-14",
    startDate: "2027-05-01",
    endDate: "2027-05-02",
    status: "open",
  },
  {
    id: "sci-skegness-2027",
    name: "SCI Sports Mini & Junior Skegness Rugby Festival 2027",
    organiser: "SCI Sports",
    type: "tour package",
    dates: "2–5 Apr 2027",
    datesConfirmed: true,
    place: "Skegness, Lincolnshire",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U14","U15"],
    format: "Festival for up to 70 teams, staying in caravans.",
    url: "https://www.scisportsrugbyfestivals.co.uk/",
    sourceUrl: "https://www.scisportsrugbyfestivals.co.uk/",
    lastChecked: "2026-10-14",
    startDate: "2027-04-02",
    endDate: "2027-04-05",
    status: "open",
  },
  {
    id: "tigers-challenge-2027",
    name: "Tigers Challenge 2027",
    organiser: "Leicester Tigers with ESF Events",
    type: "tour package",
    dates: "Dates TBC (usually Apr–May)",
    datesConfirmed: false,
    place: "Butlin's Bognor Regis / Minehead",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U12 girls","U14 girls"],
    format: "Festival weekend with a stay at Butlin’s.",
    url: "https://www.tigerschallenge.co.uk",
    sourceUrl: "https://www.tigerschallenge.co.uk",
    lastChecked: "2026-10-14",
    startDate: null,
    endDate: null,
    usualMonths: ["2027-04","2027-05"],
    status: "open",
  },
  {
    id: "norfolk-easter-2027",
    name: "Norfolk Easter Rugby Festival",
    organiser: "Sports Tours Ltd",
    type: "tour package",
    dates: "2–5 Apr 2027",
    datesConfirmed: true,
    place: "Wymondham / Hemsby, Norfolk",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U14","U15"],
    format: "Rugby and accommodation package. Match format: check with the organiser.",
    url: "https://www.sports-tours.co.uk/tournament/norfolk-easter-rugby-festival/",
    sourceUrl: "https://www.sports-tours.co.uk/rugby/",
    lastChecked: "2026-10-14",
    startDate: "2027-04-02",
    endDate: "2027-04-05",
    status: "open",
  },
  {
    id: "norfolk-girls-easter-2027",
    name: "Norfolk Girls Easter Rugby Festival",
    organiser: "Sports Tours Ltd",
    type: "tour package",
    dates: "2–5 Apr 2027",
    datesConfirmed: true,
    place: "Wymondham / Hemsby, Norfolk",
    country: "England",
    ageGroups: ["U12 girls","U14 girls","U16 girls"],
    format: "Rugby and accommodation package. Match format: check with the organiser.",
    url: "https://www.sports-tours.co.uk/tournament/norfolk-girls-rugby-festival/",
    sourceUrl: "https://www.sports-tours.co.uk/rugby/",
    lastChecked: "2026-10-14",
    startDate: "2027-04-02",
    endDate: "2027-04-05",
    status: "open",
  },
  {
    id: "norfolk-may-2027",
    name: "Norfolk Rugby Festival (May)",
    organiser: "Sports Tours Ltd",
    type: "tour package",
    dates: "30 Apr–3 May 2027",
    datesConfirmed: true,
    place: "North Walsham / Hemsby, Norfolk",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U14","U15"],
    format: "Rugby and accommodation package. Match format: check with the organiser.",
    url: "https://www.sports-tours.co.uk/tournament/norfolk-rugby-festival/",
    sourceUrl: "https://www.sports-tours.co.uk/rugby/",
    lastChecked: "2026-10-14",
    startDate: "2027-04-30",
    endDate: "2027-05-03",
    status: "open",
  },
  {
    id: "west-country-challenge-2027",
    name: "West Country Challenge 2027",
    organiser: "Burleigh Travel",
    type: "tour package",
    dates: "2–5 Apr 2027",
    datesConfirmed: true,
    place: "Somerset",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U14","U15"],
    format: "Boys’ festival. Match format: check with the organiser.",
    url: "https://burleightravel.co.uk/tour-proposal/west-country-challenge-2027/",
    sourceUrl: "https://www.burleightravel.co.uk/sports-festivals/rugby-festivals-2/",
    lastChecked: "2026-10-14",
    startDate: "2027-04-02",
    endDate: "2027-04-05",
    status: "open",
  },
  {
    id: "rugby-for-heroes-2027",
    name: "Rugby for Heroes 2027",
    organiser: "Burleigh Travel",
    type: "tour package",
    dates: "2–4 Apr 2027",
    datesConfirmed: true,
    place: "PGL Wiltshire",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U12 girls","U14 girls"],
    format: "Festival with a stay at a PGL activity centre.",
    url: "https://burleightravel.co.uk/tour-proposal/the-rugby-for-heroes-u7-u12-festival-2027/",
    sourceUrl: "https://www.burleightravel.co.uk/sports-festivals/rugby-festivals-2/",
    lastChecked: "2026-10-14",
    startDate: "2027-04-02",
    endDate: "2027-04-04",
    status: "open",
  },
  {
    id: "jurassic-coast-2027",
    name: "Jurassic Coast Festival 2027",
    organiser: "Burleigh Travel",
    type: "tour package",
    dates: "9–11 Apr 2027",
    datesConfirmed: true,
    place: "PGL Dorset",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U12 girls"],
    format: "Festival with a stay at a PGL activity centre.",
    url: "https://burleightravel.co.uk/wp-content/uploads/2026/04/Jurassic-Coast-Festival-2027-U7-U14-WEBSITE.pdf",
    sourceUrl: "https://www.burleightravel.co.uk/sports-festivals/rugby-festivals-2/",
    lastChecked: "2026-10-14",
    startDate: "2027-04-09",
    endDate: "2027-04-11",
    status: "open",
  },
  {
    id: "big-devon-bash-2027",
    name: "The Big Devon Bash 2027",
    organiser: "Burleigh Travel",
    type: "tour package",
    dates: "30 Apr–3 May 2027",
    datesConfirmed: true,
    place: "Devon",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U14","U15","U12 girls","U14 girls","U16 girls"],
    format: "Festival, with a separate girls’ festival the same weekend.",
    url: "https://burleightravel.co.uk/tour-proposal/the-big-devon-bash-2027/",
    sourceUrl: "https://www.burleightravel.co.uk/sports-festivals/rugby-festivals-2/",
    lastChecked: "2026-10-14",
    startDate: "2027-04-30",
    endDate: "2027-05-03",
    status: "open",
  },
  {
    id: "rugby-by-the-sea-2027",
    name: "Rugby by the Sea 2027",
    organiser: "Burleigh Travel",
    type: "tour package",
    dates: "30 Apr–3 May 2027",
    datesConfirmed: true,
    place: "Bridlington, East Yorkshire",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U14"],
    format: "Boys’ festival. Match format: check with the organiser.",
    url: "https://burleightravel.co.uk/wp-content/uploads/2026/03/Rugby-by-the-Sea-bridlington-2027-Website.pdf",
    sourceUrl: "https://www.burleightravel.co.uk/sports-festivals/rugby-festivals-2/",
    lastChecked: "2026-10-14",
    startDate: "2027-04-30",
    endDate: "2027-05-03",
    status: "open",
  },
  {
    id: "revel-devon-2027",
    name: "Revel Rugby Devon 2027",
    organiser: "Revel Rugby (Revel Tours)",
    type: "tour package",
    dates: "2–5 Apr 2027",
    datesConfirmed: true,
    place: "North Devon coast",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16"],
    format: "Festival weekend for girls and boys. Match format: check with the organiser.",
    url: "https://revelrugby.com/festivals/revel-rugby-devon-2027",
    sourceUrl: "https://revelrugby.com/festivals",
    lastChecked: "2026-10-14",
    startDate: "2027-04-02",
    endDate: "2027-04-05",
    status: "open",
  },
  {
    id: "revel-north-wales-2027",
    name: "Revel Rugby North Wales 2027",
    organiser: "Revel Rugby (Revel Tours)",
    type: "tour package",
    dates: "2–5 Apr 2027 (rugby 3–4 Apr)",
    datesConfirmed: true,
    place: "North Wales coast",
    country: "Wales",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16"],
    format: "Festival weekend for girls and boys. Match format: check with the organiser.",
    url: "https://revelrugby.com/festivals/revel-rugby-north-wales",
    sourceUrl: "https://revelrugby.com/festivals",
    lastChecked: "2026-10-14",
    startDate: "2027-04-02",
    endDate: "2027-04-05",
    status: "open",
  },
  {
    id: "revel-cheddar-2027",
    name: "Revel Rugby Cheddar 2027",
    organiser: "Revel Rugby (Revel Tours)",
    type: "tour package",
    dates: "23–26 Apr 2027 (rugby 25 Apr)",
    datesConfirmed: true,
    place: "Cheddar, Somerset",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U14","U15"],
    format: "Festival weekend. Match format: check with the organiser.",
    url: "https://revelrugby.com/festivals/revel-rugby-cheddar-2027",
    sourceUrl: "https://revelrugby.com/festivals",
    lastChecked: "2026-10-14",
    startDate: "2027-04-23",
    endDate: "2027-04-26",
    status: "open",
  },
  {
    id: "revel-cheshire-2027",
    name: "Revel Rugby Cheshire 2027",
    organiser: "Revel Rugby (Revel Tours)",
    type: "tour package",
    dates: "30 Apr–3 May 2027 (rugby 1–2 May)",
    datesConfirmed: true,
    place: "Ellesmere Port, Cheshire",
    country: "England",
    ageGroups: ["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16"],
    format: "U7–U11 play non-competitive games. U12 and up play group games, then Cup, Challenge Cup and Shield.",
    url: "https://revelrugby.com/festivals/revel-rugby-cheshire-2027",
    sourceUrl: "https://revelrugby.com/festivals/revel-rugby-cheshire-2027",
    lastChecked: "2026-10-14",
    startDate: "2027-04-30",
    endDate: "2027-05-03",
    status: "open",
  },
  {
    id: "hilversum-youth-2027",
    name: "Hilversum International Youth Rugby Festival",
    organiser: "Rugby Club Hilversum (travel packages from Edwin Doran, Burleigh Travel, Sports Tours Ltd and Revel Rugby)",
    type: "tour package",
    dates: "Sun 28 Mar 2027 (Easter)",
    datesConfirmed: false,
    place: "Hilversum",
    country: "Netherlands",
    ageGroups: ["U13","U14","U15","U16","U17","U18"],
    format: "U13–U18 play 15-a-side. 75 or more teams from across Europe.",
    url: "https://www.edwindoran.com/tours/hilversum-international-youth-rugby-festival/",
    sourceUrl: "https://www.edwindoran.com/tours/hilversum-international-youth-rugby-festival/",
    lastChecked: "2026-10-14",
    startDate: null,
    endDate: null,
    usualMonths: ["2027-03"],
    status: "open",
  },
  {
    id: "portugal-youth-festival-2027",
    name: "Portugal Youth Rugby Festival",
    organiser: "Sports Tours Ltd",
    type: "tour package",
    dates: "2–5 Apr 2027",
    datesConfirmed: true,
    place: "Lisbon",
    country: "Portugal",
    ageGroups: ["U13","U15","U17","U19","U15 girls","U19 girls"],
    format: "Rugby and accommodation package. Match format: check with the organiser.",
    url: "https://www.sports-tours.co.uk/tournament/portugal-youth-rugby-festival/",
    sourceUrl: "https://www.sports-tours.co.uk/rugby/",
    lastChecked: "2026-10-14",
    startDate: "2027-04-02",
    endDate: "2027-04-05",
    status: "open",
  },
  {
    id: "ghent-easter-2027",
    name: "Ghent Easter Rugby Festival",
    organiser: "Sports Tours Ltd",
    type: "tour package",
    dates: "26–29 Mar 2027",
    datesConfirmed: true,
    place: "Ghent",
    country: "Belgium",
    ageGroups: ["U17","U18","U19","Veterans"],
    format: "Rugby and accommodation package. Match format: check with the organiser.",
    url: "https://www.sports-tours.co.uk/tournament/ghent-rugby-festival/",
    sourceUrl: "https://www.sports-tours.co.uk/rugby/",
    lastChecked: "2026-10-14",
    startDate: "2027-03-26",
    endDate: "2027-03-29",
    status: "open",
  },
  {
    id: "rc-gouda-2027",
    name: "RC Gouda Festival 2027",
    organiser: "Burleigh Travel",
    type: "tour package",
    dates: "30 Apr–3 May 2027",
    datesConfirmed: true,
    place: "Gouda",
    country: "Netherlands",
    ageGroups: ["U15"],
    format: "Festival. Match format: check with the organiser.",
    url: "https://burleightravel.co.uk/wp-content/uploads/2026/03/RC-Gouda-Festival-2027-Website.pdf",
    sourceUrl: "https://www.burleightravel.co.uk/sports-festivals/rugby-festivals-2/",
    lastChecked: "2026-10-14",
    startDate: "2027-04-30",
    endDate: "2027-05-03",
    status: "open",
  },
  {
    id: "rayburn-costa-brava",
    name: "Rugby tour to Barcelona / Costa Brava",
    organiser: "Rayburn Tours",
    type: "tour package",
    dates: "You choose the dates (2026–27 tours can be booked now)",
    datesConfirmed: false,
    datesFlexible: true, // WT-26 QA: the group picks its dates, so no "Dates TBC" badge
    place: "Costa Brava (near Barcelona)",
    country: "Spain",
    ageGroups: ["Youth (school groups)"],
    format: "5 days and 4 nights, with two matches against teams of the same age.",
    url: "https://www.rayburntours.com/sports-tours/trips/rugby-tours-to-spain/",
    sourceUrl: "https://www.rayburntours.com/sports-tours/trips/rugby-tours-to-spain/",
    lastChecked: "2026-10-14",
    startDate: null,
    endDate: null,
    status: "open",
  },
  {
    id: "bournemouth-7s-2027",
    name: "Bournemouth 7s Festival 2027",
    organiser: "Bournemouth 7s Festival",
    type: "tournament",
    dates: "28–30 May 2027",
    datesConfirmed: false,
    place: "Christchurch, Dorset",
    country: "England",
    ageGroups: ["Adults (18+)"],
    format: "7-a-side, with at least 10 players per team.",
    url: "https://www.bournemouth7s.com/rugby",
    sourceUrl: "https://www.bournemouth7s.com/",
    lastChecked: "2026-10-14",
    startDate: "2027-05-28",
    endDate: "2027-05-30",
    status: "recheck",
    statusNote: "Official site blocked automated checks; details came from search-indexed text. Re-check by hand, then set status to open.",
  },
  {
    id: "kinsale-7s-2027",
    name: "Kinsale 7s 2027",
    organiser: "Kinsale RFC",
    type: "tournament",
    dates: "1–2 May 2027",
    datesConfirmed: true,
    place: "Kinsale, Co. Cork",
    country: "Ireland",
    ageGroups: ["Adult men (social)","Adult women (social)"],
    format: "7-a-side. 12 players plus a manager per team. The elite men’s section is by invitation only.",
    url: "https://www.kinsale7s.com",
    sourceUrl: "https://www.kinsale7s.com",
    lastChecked: "2026-10-14",
    startDate: "2027-05-01",
    endDate: "2027-05-02",
    status: "open",
  },
  {
    id: "amsterdam-7s-2027",
    name: "Amsterdam Rugby 7s 2027 (tour package)",
    organiser: "Burleigh Travel",
    type: "tour package",
    dates: "Dates TBC (usually early June)",
    datesConfirmed: false,
    place: "Amsterdam",
    country: "Netherlands",
    ageGroups: ["Adult men","Adult women","Veterans"],
    format: "7-a-side tournament.",
    url: "https://burleightravel.co.uk/enquiries/",
    sourceUrl: "https://www.burleightravel.co.uk/sports-festivals/rugby-festivals-2/",
    lastChecked: "2026-10-14",
    startDate: null,
    endDate: null,
    usualMonths: ["2027-06"],
    status: "open",
  },
  {
    id: "ipf-vets-day-2027",
    name: "IPF Vets Day 2027",
    organiser: "RFU Injured Players Foundation",
    type: "tournament",
    dates: "10 Apr 2027 (TBC)",
    datesConfirmed: false,
    place: "Twickenham, London",
    country: "England",
    ageGroups: ["Adult men veterans","Over-50s (Super Vets)"],
    format: "15-a-side. Each team pledges to raise money for the Injured Players Foundation. Applications closed in July 2026.",
    url: "https://www.rfuipf.org.uk/get-involved-help-the-ipf/events/your-club-or-team/ipf-vets-day",
    sourceUrl: "https://www.rfuipf.org.uk/get-involved-help-the-ipf/events/your-club-or-team/ipf-vets-day",
    lastChecked: "2026-10-14",
    startDate: "2027-04-10",
    endDate: "2027-04-10",
    status: "closed",
    statusNote: "Applications appear to have closed on 5 Jul 2026.",
  },
  {
    id: "ipf-womens-day-2027",
    name: "IPF Women's Tournament Day 2027",
    organiser: "RFU Injured Players Foundation",
    type: "tournament",
    dates: "6 Jun 2027 (TBC)",
    datesConfirmed: false,
    place: "Twickenham, London",
    country: "England",
    ageGroups: ["Adult women"],
    format: "15-a-side, 6 teams. Each team pledges to raise money for the Injured Players Foundation. Applications closed on 5 Jul 2026.",
    url: "https://www.rfuipf.org.uk/get-involved-help-the-ipf/events/your-club-or-team/womens-tournament-day-2026",
    sourceUrl: "https://www.rfuipf.org.uk/get-involved-help-the-ipf/events/your-club-or-team/womens-tournament-day-2026",
    lastChecked: "2026-10-14",
    startDate: "2027-06-06",
    endDate: "2027-06-06",
    status: "closed",
    statusNote: "Applications closed on 5 Jul 2026.",
  },
];
